<script setup lang="ts">
import { computed, watch } from 'vue';
import { useForm } from 'vee-validate';
import { useErhlLoanAppProcess } from '@/helpers/useErhlLoanAppProcess';
import { companySchema } from '@/validation';
import type { CompanyDetails } from '@/types';

const props = defineProps<{
  modelValue: CompanyDetails;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: CompanyDetails): void;
}>();

const { loanPropertyAddresses } = useErhlLoanAppProcess();

const schema = computed(() => companySchema());

const { meta, submitCount, values, handleSubmit, resetForm } = useForm<CompanyDetails>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: { ...props.modelValue },
});

const abnAcnFieldLabel = computed(() => (values.hasRegisteredABN === true ? 'ABN' : 'ACN'));
const abnAcnMaxLength = computed(() => (values.hasRegisteredABN === true ? 11 : 9));

const update = handleSubmit(
  (formValues) => {
    emit('update:modelValue', { ...formValues });
    return true;
  },
  () => false,
);

const cancel = () => {
  resetForm({ values: { ...props.modelValue } });
};

watch(
  () => props.modelValue,
  (company) => {
    if (company) {
      resetForm({ values: { ...company } });
    }
  },
);

defineExpose({ update, cancel, resetForm, meta });
</script>

<template>
  <form>
    <div>
      <BxField v-slot="{ field }" name="companyName">
        <BxInput v-bind="field" id="company-name" class="form-control" label="Company name" label-type="floating" />
      </BxField>
      <BxField v-slot="{ field }" name="hasRegisteredABN">
        <BxRadio
          v-bind="field"
          id="company-has-abn"
          label="Does the company have a registered ABN?"
          label-type="default"
          boolean
          class="form-control"
        />
      </BxField>
      <div v-if="values.hasRegisteredABN !== undefined" class="form-group mt-6">
        <BxField v-slot="{ field }" name="abnAcnNumber">
          <BxInput
            v-bind="field"
            id="company-abn-acn"
            class="form-control !mt-0"
            :label="abnAcnFieldLabel"
            label-type="floating"
            :maxlength="abnAcnMaxLength"
          />
        </BxField>
      </div>
      <BxField v-if="values.hasRegisteredABN" v-slot="{ field }" name="isTrading">
        <BxRadio
          v-bind="field"
          id="company-is-trading"
          label="Is the company currently trading?"
          label-type="default"
          boolean
          class="form-control"
        />
      </BxField>
      <BxField v-slot="{ field }" name="registeredAddress">
        <BxAddressInput
          v-bind="field"
          id="company-registered-address"
          class="form-control"
          label="Registered address"
          label-type="floating"
          :suggested-addresses="loanPropertyAddresses"
        />
      </BxField>
      <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
        Please review the error messages above.
      </BxErrorPanel>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.form-control {
  margin-top: 1.5rem;
}
</style>
